/**
 * Hook pour les verifications d'acces (modules, features de role)
 *
 * Verifications d'acces de l'utilisateur courant a partir de la session.
 *
 * Les fonctions retournent des booleens (pas null), car les donnees
 * sont garanties READY par SessionProvider.
 *
 * Le flag isFullAccess est calcule cote backend et transmis dans la session,
 * evitant de dupliquer la liste des roles a acces complet cote frontend.
 *
 * @module usePermissions
 * @author Equipe Brydge
 * @since 3.0.0
 */

import {useCallback} from 'react';
import useSessionStore from 'packages/common/stores/sessionStore';
import {ADMIN_SCOPES} from 'packages/common/config/adminSections';

export const usePermissions = () => {
  const userRole = useSessionStore((s) => s.userRole);
  const userFeatures = useSessionStore((s) => s.userFeatures);
  const modules = useSessionStore((s) => s.modules);
  const isFullAccess = useSessionStore((s) => s.isFullAccess);
  const adminAccess = useSessionStore((s) => s.adminAccess);

  const isSuperAdmin = adminAccess?.isSuperAdmin === true;

  /**
   * Vérifie l'accès à une section du panel admin.
   * Le super admin a implicitement toutes les sections.
   */
  const hasAdminSection = useCallback(
      (sectionKey) => {
        if (isSuperAdmin) return true;
        return (adminAccess?.sections || []).some((section) => section.key === sectionKey);
      },
      [isSuperAdmin, adminAccess],
  );

  /**
   * Vérifie l'accès en écriture (mode gestion) à une section.
   * Le backend reste l'autorité : les méthodes mutantes sont refusées en
   * lecture seule quoi qu'affiche l'UI.
   */
  const canManageSection = useCallback(
      (sectionKey) => {
        if (isSuperAdmin) return true;
        return (adminAccess?.sections || []).some((section) => section.key === sectionKey && section.mode === 'manage');
      },
      [isSuperAdmin, adminAccess],
  );

  /**
   * Vérifie l'accès en écriture à une section sur toutes ses données.
   * Certaines actions, comme l'émission d'une clé d'intégration, sont refusées
   * par le backend à un accès limité à un portefeuille, même en mode gestion :
   * l'interface ne doit pas les proposer.
   */
  const canManageSectionFully = useCallback(
      (sectionKey) => {
        if (isSuperAdmin) return true;
        return (adminAccess?.sections || []).some(
            (section) => section.key === sectionKey && section.mode === 'manage' && section.scope === ADMIN_SCOPES.ALL,
        );
      },
      [isSuperAdmin, adminAccess],
  );

  const hasModuleAccess = useCallback(
      (moduleKey) => modules[moduleKey] === true,
      [modules],
  );

  const hasFeatureAccess = useCallback(
      (featureKey) => {
        if (isFullAccess) return true;
        return userFeatures.includes(featureKey);
      },
      [isFullAccess, userFeatures],
  );

  const hasAnyFeatureAccess = useCallback(
      (featureKeys) => {
        if (isFullAccess) return true;
        return featureKeys.some((k) => userFeatures.includes(k));
      },
      [isFullAccess, userFeatures],
  );

  const hasAllFeatureAccess = useCallback(
      (featureKeys) => {
        if (isFullAccess) return true;
        return featureKeys.every((k) => userFeatures.includes(k));
      },
      [isFullAccess, userFeatures],
  );

  const hasAnyFeatureInModule = useCallback(
      (modulePrefix) => {
        if (isFullAccess) return true;
        return userFeatures.some((f) => f.startsWith(`${modulePrefix}.`) || f === modulePrefix);
      },
      [isFullAccess, userFeatures],
  );

  return {
    hasModuleAccess,
    hasFeatureAccess,
    hasAnyFeatureAccess,
    hasAllFeatureAccess,
    hasAnyFeatureInModule,
    hasAdminSection,
    canManageSection,
    canManageSectionFully,
    isSuperAdmin,
    adminAccess,
    userRole,
    features: userFeatures,
  };
};
